// Every timestamp is shown in the company timezone (CLAUDE.md rule 6), in the user's language.

const LOCALES: Record<string, string> = { en: 'en-MY', ms: 'ms-MY', zh: 'zh-Hans-MY' };

export function localeTag(language: string): string {
  return LOCALES[language] ?? 'en-MY';
}

/** "3 min ago" / "3 分钟前" — null when the value is missing. */
export function relativeTime(
  iso: string | null | undefined,
  language: string,
  now: number = Date.now(),
): string | null {
  if (!iso) return null;
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(localeTag(language), { numeric: 'auto', style: 'short' });
  const abs = Math.abs(seconds);
  if (abs < 60) return rtf.format(seconds, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
  return rtf.format(Math.round(seconds / 86400), 'day');
}

/** The instant a calendar date (YYYY-MM-DD) starts in a timezone, as ISO — for "from/to" filters on timestamps. */
export function startOfDayIso(date: string, timeZone: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d);
  // offset of the zone at an instant: the zone's wall clock read as if it were UTC, minus the instant
  const offset = (at: number) => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
        .formatToParts(new Date(at))
        .map((p) => [p.type, p.value]),
    );
    const wall = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second),
    );
    return wall - at;
  };
  // two passes settle days where the offset changes (DST)
  let at = guess - offset(guess);
  at = guess - offset(at);
  return new Date(at).toISOString();
}

/** "2026-09-12 09:12" in the company timezone — reports and exports (sortable, unambiguous). */
export function isoMinuteIn(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null;
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
      .formatToParts(new Date(iso))
      .map((x) => [x.type, x.value]),
  );
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
}

/** Today's date (YYYY-MM-DD) in a timezone. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone }).format(now);
}

/** "Tue 15 Sep" for a calendar date (YYYY-MM-DD, already in the company timezone). */
export function calendarDate(date: string | null | undefined, language: string): string | null {
  if (!date) return null;
  return new Intl.DateTimeFormat(localeTag(language), {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(`${date}T00:00:00Z`));
}

/**
 * "12 Sep, 09:12" in the company timezone; with `year`, "12 Sep 2026, 09:12" (an asset's lifecycle spans years —
 * owner, 2026-09-24).
 */
export function dateTime(
  iso: string | null | undefined,
  language: string,
  timeZone: string,
  opts: { year?: boolean } = {},
): string | null {
  if (!iso) return null;
  return new Intl.DateTimeFormat(localeTag(language), {
    timeZone,
    day: 'numeric',
    month: 'short',
    ...(opts.year ? { year: 'numeric' as const } : {}),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}
